import type { SoftphoneConferenceProps } from './index.types';
import { ReactComponent as OnCallIcon } from 'Assets/icons/on-call.svg';
import { ReactComponent as OnHoldIcon } from 'Assets/icons/on-hold.svg';
import { SoftphoneCollapsibleItems } from 'Modules/softphone/components/SoftphoneCollapsibleItems';
import { SoftphoneCollapsibleCallItem } from 'Modules/softphone/components/SoftphoneCollapsibleItems/SoftphoneCollapsibleCallItem';
import { useSoftphoneContact } from 'Modules/softphone/hooks/useSoftphoneContact';
import { useSoftphoneStore } from 'Modules/softphone/store';
import type { CallSession } from 'Modules/softphone/types';
import React from 'react';

/**
 * Resolves the display name for a conference participant via useSoftphoneContact,
 * matching the same pattern used by SoftphoneHeader for the current call.
 *
 * Uses forwardRef so SoftphoneCollapsibleItems can inject refs via React.cloneElement
 * for keyboard navigation focus management.
 */
const ParticipantItem = React.forwardRef<
  HTMLButtonElement,
  {
    participant: CallSession;
    testID: string;
    [key: string]: unknown;
  }
>(({ participant, testID, ...rest }, ref) => {
  const { displayName } = useSoftphoneContact(participant.id, participant);
  const resolvedName =
    displayName ?? participant.displayName ?? participant.phoneNumber;

  return (
    <SoftphoneCollapsibleCallItem
      ref={ref}
      displayName={resolvedName}
      ariaLabel={`${resolvedName} in conference`}
      showHoverState={false}
      className="cursor-default"
      testID={testID}
      {...rest}
    />
  );
});

ParticipantItem.displayName = 'ParticipantItem';

/**
 * SoftphoneConference - Displays conference participants in an expandable dropdown.
 *
 * Thin wrapper around SoftphoneCollapsibleCallGroup that maps conference-specific
 * props to the generic collapsible interface.
 */
export const SoftphoneConference = ({
  participants,
  isOnHold = false,
  boundaryRef,
  testID,
}: SoftphoneConferenceProps): React.ReactElement => {
  const [isExpanded, toggleCollapsible, closeCollapsible] = useSoftphoneStore(
    (state) =>
      [
        state.expandedCollapsible === 'conference',
        state.toggleCollapsible,
        state.closeCollapsible,
      ] as const
  );

  const componentTestId = testID ? `${testID}-conference` : `conference`;

  const title = 'Conference call';
  const subtitle = isOnHold
    ? 'on hold'
    : `with ${participants.length} people`;
  const icon = isOnHold ? OnHoldIcon : OnCallIcon;

  const handleToggle = () => {
    toggleCollapsible('conference');
  };

  return (
    <SoftphoneCollapsibleItems
      title={title}
      subtitle={subtitle}
      icon={icon}
      isExpanded={isExpanded}
      onToggle={handleToggle}
      closeCollapsible={closeCollapsible}
      boundaryRef={boundaryRef}
      listAriaLabel="Conference participants"
      itemCount={participants.length}
      testID={componentTestId}
    >
      {participants.map((participant) => (
        <ParticipantItem
          key={participant.id}
          participant={participant}
          testID={componentTestId}
        />
      ))}
    </SoftphoneCollapsibleItems>
  );
};

SoftphoneConference.displayName = 'SoftphoneConference';
